import React, { useCallback, useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { useForm } from 'react-hook-form';
import { FaTimes } from 'react-icons/fa';
import { ReactComponent as ImageAddIcon } from '../../assets/svg/ImageAddIcon.svg';
import { Button, TextField } from '@mui/material';
import { createFont, editLanguage, getImage, updateFont, updateTranslation, uploadFile, uploadFontFile } from '../../server/server';
import { baseUrl } from '../../server/connections';
import { checkAllowedExtensionsForFonts } from '../../Constants';

function TranslationModal({ open, onClose, language }: any) {
	const [search, setSearch] = useState('');
	const [content, setContent] = useState(language?.content);
	const [isJsonMode, setIsJsonMode] = useState(false);
	const [jsonContent, setJsonContent] = useState('');
	const [jsonError, setJsonError] = useState('');

	useEffect(() => {
		setContent(language?.content);
		setJsonContent(JSON.stringify(language?.content, null, 2));
	}, [language]);

	const filterTranslation = useCallback(
		(e: string) => {
			return e.toLowerCase().includes(search.toLowerCase()) || content[e].toLowerCase().includes(search.toLowerCase());
		},
		[content, search]
	);

	const toggleJsonMode = () => {
		if (!isJsonMode) {
			// Switching to JSON mode
			setJsonContent(JSON.stringify(content, null, 2));
			setJsonError('');
		} else {
			// Switching back to normal mode
			try {
				const parsedContent = JSON.parse(jsonContent);
				setContent(parsedContent);
				setJsonError('');
			} catch (error) {
				setJsonError('Invalid JSON format. Please fix before switching back.');
				return; // Prevent mode switch if JSON is invalid
			}
		}
		setIsJsonMode(!isJsonMode);
	};

	const handleJsonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setJsonContent(e.target.value);
		try {
			JSON.parse(e.target.value);
			setJsonError('');
		} catch (error) {
			setJsonError('Invalid JSON format');
		}
	};

	const onSubmit = async () => {
		let submittableContent = content;

		if (isJsonMode) {
			try {
				submittableContent = JSON.parse(jsonContent);
			} catch (error) {
				setJsonError('Invalid JSON format. Cannot submit.');
				return;
			}
		}

		const newLanguageData = {
			id: language.id,
			content: submittableContent,
		};

		const resp = await updateTranslation(newLanguageData);
		if (resp.status === 200) {
			alert('Language updated successfully');
			onClose();
		} else {
			console.log(resp);
		}
	};

	return (
		<ReactModal
			ariaHideApp={false}
			isOpen={open}
			onAfterOpen={() => {}}
			onRequestClose={onClose}
			className="no-scroll"
			style={{
				overlay: {
					zIndex: 5,
					backdropFilter: 'brightness(30%)',
				},
				content: {
					padding: 0,
					opacity: 1,
					width: 1000,
					margin: 'auto',
					backgroundColor: 'transparent',
					height: '100%',
					maxHeight: '100%',
					border: 'none',
					boxShadow: 'none',
					overflow: 'auto',
					paddingTop: 15,
					paddingBottom: 15,
				},
			}}
		>
			<div className="max-h-[600px] w-full">
				<div className="flex flex-1 flex-col gap-5 rounded-[10px] bg-babyBlue px-[40px] py-[30px]">
					<div className="flex w-full flex-row items-center justify-between">
						<p className="text-[30px] font-semibold leading-[38px] text-black">Translations</p>
						<div className="flex flex-row gap-5">
							<Button variant="contained" onClick={toggleJsonMode} style={{ backgroundColor: '#ff9f1c', color: 'white' }}>
								{isJsonMode ? 'Normal View' : 'JSON Editor'}
							</Button>
							<Button
								variant="contained"
								onClick={(e: any) => {
									e.preventDefault();
									onClose();
								}}
								style={{ backgroundColor: '#2ec4b6', color: 'white' }}
							>
								Cancel
							</Button>
							<Button variant="contained" type="submit" style={{ backgroundColor: '#2ec4b6', color: 'white' }} onClick={onSubmit}>
								Submit
							</Button>
						</div>
					</div>

					{!isJsonMode ? (
						<>
							<div className="">
								<TextField label="Search Translation" variant="outlined" value={search} onChange={(e) => setSearch(e.target.value)} fullWidth margin="normal" />
							</div>
							<div className="flex flex-col gap-2">
								{Object.keys(content ?? {})
									.filter(filterTranslation)
									.map((contentKey) => (
										<div className="border rounded-xl p-5 border-gray" key={contentKey}>
											<div>
												<label htmlFor="" className="text-mediumGrey">
													{contentKey}
												</label>
												<TextField variant="outlined" value={content[contentKey]} onChange={(e) => setContent({ ...content, [contentKey]: e.target.value })} fullWidth margin="normal" />
											</div>
										</div>
									))}
							</div>
						</>
					) : (
						<div className="flex flex-col gap-2">
							<div className="border rounded-xl p-5 border-gray">
								<textarea className="w-full h-[400px] p-3 font-mono text-sm border rounded bg-[transparent]" value={jsonContent} onChange={handleJsonChange} />
								{jsonError && <div className="text-red-500 mt-2">{jsonError}</div>}
							</div>
						</div>
					)}
				</div>
			</div>
		</ReactModal>
	);
}

export default TranslationModal;
